import { useEffect, useRef } from "react";
import { string, number, bool, object, func } from "prop-types";
import classNames from "classnames";
import Headline from "../Headline/component";
import CardImageWrap from "../CardImageWrap/component";
import { Label } from "../../../Label/component";
import LinkWrap from "../../../LinkWrap/component";
import Metadata from "../Metadata/component";
import {
    createLinkProps,
    replaceWithSmallSquareDerivative,
} from "../../../../utils/dataTransform/utils";
import { useNormalizeClass } from "../../../../hooks/useNormalizeClass";

/**
 * Icon Description card for sports icon page for olympics
 */

const CardIconDescription = ({
    imageURL,
    external,
    sourceId,
    title,
    url,
    id,
    className,
    author = {
        name: "",
        image: "",
    },
    displayComments,
    flag,
    flagId,
    focus,
    baseSection,
    subSection,
    updateTime,
    departments,
    metadataId,
    cardClickHandler,
    description,
    videoDuration,
    videoAirDate,
    videoCardOverlayType,
    itemType,
    hideAuthorInfo,
    videoStreamType,
}) => {
    const domNode = useRef(null);
    /**
     * Fires after component renders. Handle any post-rendering logic
     *
     * @returns {void}
     */
    useEffect(() => {
        // assign focus based on props
        if (focus !== undefined) {
            domNode.current.focus();
        }
    }, [focus]);

    const flagClassName = useNormalizeClass("flag", flag);
    const classes = classNames("card", "cardDefault", flagClassName, className);

    const labelProps = {
        flag: flag,
        external: external,
        id: flagId,
    };

    const linkProps = createLinkProps({
        sourceId,
        external,
        url,
        cardClickHandler,
    });

    const metaProps = {
        baseSection: baseSection,
        subSection: subSection,
        authorName: author.name,
        authorImage: author.image,
        updated: updateTime,
        showTimestamp: true,
        departments: departments,
        id: metadataId,
        url: url,
        displayComments: displayComments,
        hideAuthorInfo,
    };

    let imageSrcURL = replaceWithSmallSquareDerivative(imageURL);

    const imageProps = {
        url: imageSrcURL,
    };

    const videoLinkImageOverlayProps = {
        videoCardOverlayType: videoCardOverlayType ? "Minimal" : "",
        videoDuration,
        videoAirDate,
        videoStreamType,
    };

    const headlineDescription =
        description && description.length <= 1 ? null : (
            <p className="cardDefaultDescription">{description}</p>
        );

    return (
        <LinkWrap
            id={id}
            className={classNames(classes, "cardIconDescription")}
            {...linkProps}
            data-cy={`type-${itemType}`}
        >
            <div ref={domNode} className="card-content">
                <div className="card-content-top">
                    <div className="cardHeadlineImage">
                        <CardImageWrap
                            {...{ imageProps, videoLinkImageOverlayProps }}
                        />
                        <Label {...labelProps} />
                        <div className="cardHeadlineDescription">
                            <Headline text={title} />
                            {headlineDescription}
                        </div>
                    </div>
                </div>
                <div className="card-content-bottom">
                    <Metadata {...metaProps} />
                </div>
            </div>
        </LinkWrap>
    );
};

CardIconDescription.propTypes = {
    // The category that the item belongs to.
    category: string.isRequired,
    // Item ID from data aggregation layer.
    contentId: number.isRequired,
    // Is the URL external (in which case we don't handle it)?
    external: bool.isRequired,
    // Item ID from original source, e.g. CMS.
    sourceId: string.isRequired,
    // The headline to display.
    title: string.isRequired,
    // The URL that the card links to
    url: string.isRequired,
    // ID of card
    id: string.isRequired,

    // OPTIONAL
    className: string,
    // Author name and image object
    author: object,
    // Display comments for this card
    displayComments: bool,
    // Display a deck for this card; Implemented for CardContentPackage
    deck: string,
    // Indicates a description ID for this card; Implemented for CardContentPackage
    descriptionId: string,
    // Indicates a description for this card; Implemented for Tokyo 2020 Sport Icon cards
    description: string,

    flag: string,

    flagId: string,

    focus: bool,

    onClick: func,

    cardClickHandler: func,

    baseSection: string,

    subSection: string,

    updateTime: number,

    departments: object,

    metadataId: string,

    itemType: string,
};

export default CardIconDescription;
